/**
 * Source Data page — trace every parsed value back to the uploaded document.
 *
 * Shows each document with its extracted tenants/fields and confidence,
 * with a "View Source" action to see the original PDF page.
 */

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FileText, Eye, ChevronDown, ChevronRight, Loader2, AlertCircle } from "lucide-react";
import { useDealContext } from "../hooks/useDealContext";
import {
  fetchSourceAudit,
  type SourceAuditDocument,
  type SourceAuditTenant,
} from "../api/client";
import { fmtCurrency, fmtPercent, fmtPSF, fmtSF } from "../utils/format";
import { prettyValue, prettyFieldLabel } from "../utils/labels";
import SourceViewerModal from "../components/source-audit/SourceViewerModal";
import CamRecSourceCard from "../components/source-audit/CamRecSourceCard";

function confidenceColor(c: number): string {
  if (c >= 0.8) return "bg-green-500";
  if (c >= 0.5) return "bg-yellow-500";
  return "bg-red-500";
}

function confidenceLabel(c: number): string {
  if (c >= 0.8) return "High";
  if (c >= 0.5) return "Medium";
  return "Low";
}

function formatFieldValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value !== "number") return String(value);
  const n = value as number;
  if (key === "leased_sf") return fmtSF(n);
  if (key === "base_rent_annual_psf") return fmtPSF(n);
  if (key === "rent_escalation_value") return n > 0 && n < 1 ? fmtPercent(n, 2) : fmtPSF(n);
  if (key === "free_rent_months") return `${n} month${n === 1 ? "" : "s"}`;
  return String(n);
}

const FILE_TYPE_LABELS: Record<string, string> = {
  rent_roll: "Rent Roll",
  operating_statement: "Operating Statement",
  cam_rec: "CAM Reconciliation",
};

export default function SourceDataPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const { deal } = useDealContext();
  const [docs, setDocs] = useState<SourceAuditDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());
  const [viewer, setViewer] = useState<{
    docId: string; filename: string; tenantName: string | null;
  } | null>(null);

  useEffect(() => {
    if (!dealId) return;
    setLoading(true);
    fetchSourceAudit(dealId)
      .then((data) => {
        setDocs(data);
        // Auto-expand first document with parsed data
        const first = data.find((d) => d.parse_status === "complete");
        if (first) setExpandedDocs(new Set([first.id]));
      })
      .catch(() => setError("Failed to load source audit data."))
      .finally(() => setLoading(false));
  }, [dealId]);

  const toggleDoc = (id: string) => {
    setExpandedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openViewer = (docId: string, filename: string, tenantName: string | null) => {
    setViewer({ docId, filename, tenantName });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-forest-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-bark-400 text-sm">
        <AlertCircle className="w-5 h-5 mr-2" /> {error}
      </div>
    );
  }

  if (docs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-bark-400">
        <FileText className="w-8 h-8 mb-2" />
        <p className="text-sm">No documents uploaded yet.</p>
        <p className="text-xs mt-1">Upload documents on the Documents tab to see source audit data.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-6 space-y-4">
      <div className="mb-6">
        <h2 className="font-display text-xl font-semibold text-bark-800">Source Data</h2>
        <p className="text-sm text-bark-400 mt-1">
          Verify parsed values against the original uploaded documents.
        </p>
      </div>

      {docs.map((doc) => (
        <DocumentCard
          key={doc.id}
          doc={doc}
          dealId={dealId!}
          expanded={expandedDocs.has(doc.id)}
          onToggle={() => toggleDoc(doc.id)}
          onViewSource={(tenantName) => openViewer(doc.id, doc.filename, tenantName)}
        />
      ))}

      {viewer && (
        <SourceViewerModal
          dealId={dealId!}
          docId={viewer.docId}
          filename={viewer.filename}
          tenantName={viewer.tenantName}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document card
// ---------------------------------------------------------------------------

interface DocumentCardProps {
  doc: SourceAuditDocument;
  dealId: string;
  expanded: boolean;
  onToggle: () => void;
  onViewSource: (tenantName: string | null) => void;
}

function DocumentCard({ doc, dealId, expanded, onToggle, onViewSource }: DocumentCardProps) {
  const isPdf = doc.filename.toLowerCase().endsWith(".pdf");
  const hasTenants = doc.tenants.length > 0;
  const hasOpex = doc.opex_years.length > 0;
  const hasCamRec = doc.cam_rec != null;
  const parsed = doc.parse_status === "complete";

  return (
    <div className="bg-white rounded-xl border border-grove-300 overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-grove-50 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-bark-400 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-bark-400 shrink-0" />
        )}
        <FileText className="w-5 h-5 text-forest-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-bark-800 truncate">{doc.filename}</p>
          <p className="text-xs text-bark-400 mt-0.5">
            {FILE_TYPE_LABELS[doc.file_type] || doc.file_type}
            {doc.uploaded_at && ` \u00B7 Uploaded ${new Date(doc.uploaded_at).toLocaleDateString()}`}
            {hasTenants && ` \u00B7 ${doc.tenants.length} tenant${doc.tenants.length !== 1 ? "s" : ""}`}
            {hasOpex && ` \u00B7 ${doc.opex_years.length} year${doc.opex_years.length !== 1 ? "s" : ""}`}
            {hasCamRec && ` \u00B7 ${doc.cam_rec!.tenants.length} tenant${doc.cam_rec!.tenants.length !== 1 ? "s" : ""} · ${doc.cam_rec!.expense_pools.length} pool${doc.cam_rec!.expense_pools.length !== 1 ? "s" : ""}`}
          </p>
        </div>
        {parsed && doc.confidence_score != null && (
          <div className="flex items-center gap-1.5 shrink-0">
            <div className={`w-2 h-2 rounded-full ${
              doc.confidence_score >= 80 ? "bg-green-500"
                : doc.confidence_score >= 50 ? "bg-yellow-500"
                : "bg-red-500"
            }`} />
            <span className="text-xs text-bark-500 tabular-nums">{doc.confidence_score}/100</span>
          </div>
        )}
        {isPdf && parsed && (
          <button
            onClick={(e) => { e.stopPropagation(); onViewSource(null); }}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-forest-700 border border-forest-200 rounded-lg hover:bg-forest-50 transition-colors shrink-0"
          >
            <Eye className="w-3.5 h-3.5" /> View Document
          </button>
        )}
      </button>

      {/* Expanded content */}
      {expanded && parsed && hasTenants && (
        <TenantTable tenants={doc.tenants} isPdf={isPdf} onViewSource={onViewSource} />
      )}
      {expanded && parsed && hasOpex && (
        <OpexTable opexYears={doc.opex_years} isPdf={isPdf} onViewSource={onViewSource} />
      )}
      {expanded && parsed && hasCamRec && doc.cam_rec && (
        <CamRecSourceCard camRec={doc.cam_rec} isPdf={isPdf} onViewSource={onViewSource} />
      )}
      {expanded && !parsed && (
        <div className="px-5 py-4 text-sm text-bark-400 border-t border-grove-200">
          This document has not been parsed yet.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tenant table
// ---------------------------------------------------------------------------

interface TenantTableProps {
  tenants: SourceAuditTenant[];
  isPdf: boolean;
  onViewSource: (tenantName: string) => void;
}

function TenantTable({ tenants, isPdf, onViewSource }: TenantTableProps) {
  const [expandedTenant, setExpandedTenant] = useState<string | null>(null);

  return (
    <div className="border-t border-grove-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-grove-50 text-bark-500">
            <th className="text-left px-4 py-2 text-xs font-medium">Tenant</th>
            <th className="text-right px-3 py-2 text-xs font-medium">SF</th>
            <th className="text-right px-3 py-2 text-xs font-medium">Rent PSF</th>
            <th className="text-left px-3 py-2 text-xs font-medium">Lease Type</th>
            <th className="text-left px-3 py-2 text-xs font-medium">Expires</th>
            <th className="text-center px-3 py-2 text-xs font-medium">Confidence</th>
            {isPdf && <th className="text-center px-3 py-2 text-xs font-medium">Source</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-grove-200">
          {tenants.map((t) => {
            const sf = t.fields.leased_sf;
            const rent = t.fields.base_rent_annual_psf;
            const leaseType = t.fields.lease_type;
            const expires = t.fields.lease_expiration_date;
            const confValues = Object.values(t.fields).map((f) => f.confidence).filter((c) => c > 0);
            const avgConf = confValues.length > 0 ? confValues.reduce((a, b) => a + b, 0) / confValues.length : 0;
            const hasCam = t.cam_detail != null;
            const isExpanded = expandedTenant === t.tenant_name;

            return (
              <React.Fragment key={t.tenant_name}>
                <tr
                  className={`transition-colors ${hasCam ? "cursor-pointer" : ""} ${isExpanded ? "bg-grove-50" : "hover:bg-grove-50"}`}
                  onClick={() => hasCam && setExpandedTenant(isExpanded ? null : t.tenant_name)}
                >
                  <td className="px-4 py-2.5 font-medium text-bark-800">
                    <div className="flex items-center gap-1.5">
                      {hasCam && (isExpanded ? <ChevronDown className="w-3 h-3 text-bark-400" /> : <ChevronRight className="w-3 h-3 text-bark-400" />)}
                      {t.tenant_name}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-bark-600">
                    {sf ? formatFieldValue("leased_sf", sf.value) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-bark-600">
                    {rent ? formatFieldValue("base_rent_annual_psf", rent.value) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-bark-600">
                    {leaseType ? prettyValue(leaseType.value) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-bark-600 whitespace-nowrap">
                    {expires ? String(expires.value) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <div className={`w-2 h-2 rounded-full ${confidenceColor(avgConf)}`} title={confidenceLabel(avgConf)} />
                      <span className="text-xs text-bark-400 tabular-nums">{Math.round(avgConf * 100)}%</span>
                    </div>
                  </td>
                  {isPdf && (
                    <td className="px-3 py-2.5 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); onViewSource(t.tenant_name); }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-forest-700 hover:bg-forest-50 rounded-md transition-colors"
                      >
                        <Eye className="w-3 h-3" /> View
                      </button>
                    </td>
                  )}
                </tr>
                {isExpanded && hasCam && (
                  <tr>
                    <td colSpan={isPdf ? 7 : 6} className="bg-grove-50 px-6 py-3">
                      <CamDetailPanel cam={t.cam_detail!} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CamDetailPanel({ cam }: { cam: NonNullable<SourceAuditTenant["cam_detail"]> }) {
  const entries = Object.entries(cam).filter(
    ([k]) => k !== "cam_type" && k !== "confidence"
  ) as [string, { label: string; value: unknown }][];

  return (
    <div>
      <p className="text-xs font-semibold text-bark-600 mb-2">
        Reimbursement Structure: <span className="text-forest-700">{cam.cam_type ? prettyValue(cam.cam_type) : "—"}</span>
      </p>
      {entries.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
          {entries.map(([key, detail]) => (
            <div key={key} className="text-xs">
              <span className="text-bark-400">{detail.label}: </span>
              <span className="text-bark-700 font-medium">{formatCamValue(key, detail.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatCamValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    if (key.includes("pct") || key.includes("cap_pct")) return fmtPercent(value);
    if (key.includes("psf")) return fmtPSF(value);
    if (key.includes("amount") || key.includes("stop")) return fmtCurrency(value);
    if (key.endsWith("_year") && value > 1900) return String(value);
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === "object" && v?.category) ? prettyValue(v.category) : prettyValue(v)).join(", ");
  }
  return String(value);
}

// ---------------------------------------------------------------------------
// Operating statement table
// ---------------------------------------------------------------------------

interface OpexTableProps {
  opexYears: SourceAuditDocument["opex_years"];
  isPdf: boolean;
  onViewSource: (searchTerm: string) => void;
}

function OpexTable({ opexYears, isPdf, onViewSource }: OpexTableProps) {
  const [expandedYear, setExpandedYear] = useState<number | null>(null);

  return (
    <div className="border-t border-grove-200">
      {opexYears.map((yr, idx) => {
        const yearKey = yr.year ?? idx;
        const isExpanded = expandedYear === yearKey;
        return (
          <div key={yearKey} className="border-b border-grove-200 last:border-0">
            <button
              onClick={() => setExpandedYear(isExpanded ? null : (yearKey as number))}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-grove-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-bark-400" /> : <ChevronRight className="w-3.5 h-3.5 text-bark-400" />}
                <span className="text-sm font-medium text-bark-800">{yr.year || yr.period_label || `Year ${idx + 1}`}</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-bark-500">
                <span>Revenue: <strong className="text-bark-700">{fmtCurrency(yr.total_revenue)}</strong></span>
                <span>Expenses: <strong className="text-bark-700">{fmtCurrency(yr.total_expenses)}</strong></span>
                <span>NOI: <strong className="text-bark-800">{fmtCurrency(yr.noi)}</strong></span>
              </div>
            </button>
            {isExpanded && (
              <div className="px-5 pb-4">
                {yr.expense_items.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-bark-500 uppercase tracking-wider mb-1.5">Expenses</p>
                    <div className="border border-grove-200 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead><tr className="bg-grove-100">
                          <th className="text-left px-3 py-1.5 font-medium text-bark-500">Line Item</th>
                          <th className="text-left px-3 py-1.5 font-medium text-bark-500">Category</th>
                          <th className="text-right px-3 py-1.5 font-medium text-bark-500">Amount</th>
                          {isPdf && <th className="w-12" />}
                        </tr></thead>
                        <tbody className="divide-y divide-grove-100">
                          {yr.expense_items.map((item, i) => (
                            <tr key={i} className="hover:bg-grove-50">
                              <td className="px-3 py-1.5 text-bark-700">{item.label}</td>
                              <td className="px-3 py-1.5 text-bark-400">{item.category ? prettyFieldLabel(item.category) : "—"}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums text-bark-700">{fmtCurrency(item.amount)}</td>
                              {isPdf && (
                                <td className="px-2 py-1.5 text-center">
                                  <button
                                    onClick={() => onViewSource(item.label)}
                                    className="text-forest-600 hover:text-forest-800"
                                    title="View source"
                                  >
                                    <Eye className="w-3 h-3" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {yr.revenue_items.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-bark-500 uppercase tracking-wider mb-1.5">Revenue</p>
                    <div className="border border-grove-200 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <tbody className="divide-y divide-grove-100">
                          {yr.revenue_items.map((item, i) => (
                            <tr key={i} className="hover:bg-grove-50">
                              <td className="px-3 py-1.5 text-bark-700">{item.label}</td>
                              <td className="px-3 py-1.5 text-bark-400">{item.category ? prettyFieldLabel(item.category) : "—"}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums text-bark-700">{fmtCurrency(item.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
